import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster, ToastBar, toast } from 'react-hot-toast';
import { X } from 'lucide-react';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import UnifiedLogin from './pages/UnifiedLogin';
import ChangePassword from './pages/ChangePassword';
import AdminDashboard from './pages/AdminDashboard';
import EstimatesPage from './pages/EstimatesPage';
import ClosedOrdersPage from './pages/ClosedOrdersPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceForm from './pages/InvoiceForm';
import InvoiceHistory from './pages/InvoiceHistory';
import ProductManagement from './pages/ProductManagement';
import CategoryManagement from './pages/CategoryManagement';
import CustomerManagement from './pages/CustomerManagement';
import CustomerOrders from './pages/CustomerOrders';
import EmployeeManagement from './pages/EmployeeManagement';
import Reports from './pages/Reports';
import AdminReports from './pages/AdminReports';
import AdminSettings from './pages/AdminSettings';
import AdminEstimateManagement from './pages/AdminEstimateManagement';
import AdminInvoiceManagement from './pages/AdminInvoiceManagement';
import AdminClosedOrders from './pages/AdminClosedOrders';
import AdminPayments from './pages/AdminPayments';
import ProductCatalogPage from './pages/ProductCatalogPage';
import CustomersPage from './pages/CustomersPage';
import SalesCustomerList from './pages/SalesCustomerList';
import AdminProfile from './pages/AdminProfile';
import LeadManagement from './pages/LeadManagement';
import EmployeeProfile from './pages/EmployeeProfile';
import EmployeeExpenses from './pages/EmployeeExpenses';
import AdminExpenses from './pages/AdminExpenses';
import SalesSiteVisits from './pages/SalesSiteVisits';
import GuestDocumentView from './pages/GuestDocumentView';
import ProductGallery from './pages/ProductGallery';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import SalesLayout from './components/SalesLayout';
import ModernLayout from './components/ModernLayout';
import AdminLayout from './components/AdminLayout';
import SandboxBanner from './components/SandboxBanner';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Component to conditionally render navbar
const ConditionalNavbar = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin') && location.pathname !== '/login';
  const isMainDashboard = location.pathname === '/' || location.pathname.startsWith('/sales');
  const isLoginPage = location.pathname === '/login';
  const isChangePasswordPage = location.pathname === '/change-password';
  const isProfilePage = location.pathname === '/profile';
  const isGalleryPage = location.pathname === '/gallery';
  const isGuestView = location.pathname.startsWith('/view/');
  const isPrivacyPage = location.pathname === '/privacy-policy';

  if (isGuestView || isPrivacyPage) {
    return null; // No navbar for guest views or privacy policy
  }

  if (isAdminRoute) {
    return null; // No navbar for admin routes since AdminLayout has its own header
  }

  if (isLoginPage || isChangePasswordPage) {
    return null; // No navbar for login/change password pages
  }

  // Don't show navbar for main dashboard and sales routes (they have their own layout)
  if (isMainDashboard) {
    return null;
  }

  // Don't show navbar for profile and gallery pages (ModernLayout has its own header)
  if (isProfilePage || isGalleryPage) {
    return null;
  }

  return <Navbar />;
};

// Component to conditionally render sandbox banner
const ConditionalSandboxBanner = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isChangePasswordPage = location.pathname === '/change-password';
  const isGuestView = location.pathname.startsWith('/view/');

  // Don't show banner on login/change password pages or guest view
  if (isLoginPage || isChangePasswordPage || isGuestView) {
    return null;
  }

  // Show banner if user is in sandbox mode
  if (user?.isSandbox) {
    return <SandboxBanner />;
  }

  return null;
};

// Main app content that uses auth context
const AppContent = () => {
  const { user } = useAuth();

  // Add/remove body class for sandbox mode padding
  useEffect(() => {
    if (user?.isSandbox) {
      document.body.classList.add('sandbox-mode');
    } else {
      document.body.classList.remove('sandbox-mode');
    }
    return () => {
      document.body.classList.remove('sandbox-mode');
    };
  }, [user]);

  return (
    <div className="min-h-screen bg-[#F6F8F5] flex flex-col">
      <ConditionalSandboxBanner />
      <ConditionalNavbar />
      <main className="flex-grow">
        <Routes>
          {/* Public Login Routes */}
          <Route path="/login" element={<UnifiedLogin />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/view/document/:shareToken" element={<GuestDocumentView />} />
          <Route path="/view/product/:productToken" element={<ProductGallery />} />
          <Route path="/view/category/:categorySlug/:categoryId" element={<ProductGallery />} />
          <Route path="/view/category/:categoryId" element={<ProductGallery />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Redirect old admin login to new unified login */}
          <Route path="/admin/login" element={<Navigate to="/login" replace />} />

          {/* Protected Employee Routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <ModernLayout><HomePage /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ModernLayout><EmployeeProfile /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/estimates" element={
            <ProtectedRoute>
              <ModernLayout><EstimatesPage /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/estimates/edit/:id" element={
            <ProtectedRoute>
              <ModernLayout><EstimatesPage /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/closed-orders" element={
            <ProtectedRoute>
              <ModernLayout><ClosedOrdersPage /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/invoices" element={
            <ProtectedRoute>
              <ModernLayout><InvoicesPage /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/invoices/edit/:id" element={
            <ProtectedRoute>
              <ModernLayout><InvoicesPage /></ModernLayout>
            </ProtectedRoute>
          } />
          {/* Legacy routes for backward compatibility */}
          <Route path="/sales/estimate" element={
            <ProtectedRoute>
              <SalesLayout><EstimatesPage /></SalesLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/estimate/edit/:id" element={
            <ProtectedRoute>
              <SalesLayout><EstimatesPage /></SalesLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/history" element={
            <ProtectedRoute>
              <SalesLayout><EstimatesPage /></SalesLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/invoice/new" element={
            <ProtectedRoute>
              <SalesLayout><InvoicesPage /></SalesLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/invoice/edit/:id" element={
            <ProtectedRoute>
              <SalesLayout><InvoicesPage /></SalesLayout>
            </ProtectedRoute>
          } />

          {/* Protected Admin Routes */}
          <Route path="/admin/dashboard" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><AdminDashboard /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/profile" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><AdminProfile /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/products" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><ProductManagement /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/categories" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><CategoryManagement /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/customers" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><CustomerManagement /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/customers/:customerId/orders" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><CustomerOrders /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/employees" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><EmployeeManagement /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/reports" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><AdminReports /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><AdminSettings /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/estimates" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><AdminEstimateManagement /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/invoices" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><AdminInvoiceManagement /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/closed-orders" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><AdminClosedOrders /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/payments" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminPayments />
              </AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/leads" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <LeadManagement />
              </AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/expenses" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminExpenses />
              </AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/expenses" element={
            <ProtectedRoute>
              <ModernLayout><EmployeeExpenses /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/site-visits" element={
            <ProtectedRoute>
              <ModernLayout><SalesSiteVisits /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/products" element={
            <ProtectedRoute>
              <ModernLayout><ProductCatalogPage /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/sales/customers" element={
            <ProtectedRoute>
              <ModernLayout><CustomersPage /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/gallery" element={
            <ProtectedRoute>
              <ModernLayout><ProductGallery /></ModernLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/gallery" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout><ProductGallery /></AdminLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </main>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#363636',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            maxWidth: '400px'
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
            style: {
              border: '1px solid #d1fae5',
              backgroundColor: '#f0fdf4',
            }
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
            style: {
              border: '1px solid #fecaca',
              backgroundColor: '#fef2f2',
            }
          },
          loading: {
            duration: Infinity,
            iconTheme: {
              primary: '#3b82f6',
              secondary: '#fff',
            },
          }
        }}
        containerStyle={{
          top: 80,
          right: 20,
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                <div style={{ flex: 1, paddingRight: '8px' }}>{message}</div>
                {t.type !== 'loading' && (
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="flex items-center justify-center rounded-full p-1 hover:bg-gray-100 transition-colors"
                    style={{
                      color: '#9ca3af',
                      marginLeft: '4px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer'
                    }}
                    aria-label="Close"
                  >
                    <X size={16} />
                  </button>
                )}
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
    </div>
  );
};
// Main App component with providers
function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}
export default App;


